// src/lib/onboarding.js — Onboarding Ciclo B portado do monolito (porte fiel + trial).
// Modal de boas-vindas (slides por papel) + tour contextual com spotlight +
// persistência via RPC marcar_onboarding_visto() (coluna users_profile.onboarding_visto).
// Adaptações Vite: seletores da sidebar V2 (.sb-sub-item[data-page]) e dos bottom navs
// dos 3 shells; slide extra de TRIAL (módulos limitados + upsell + usuários demo).
// Replay: window.verIntroducao() (sem persistir).

import { sb } from '../api/supabaseClient.js';
import { tenant } from '../context/tenantContext.js';
import { navigate } from '../router/router.js';
import './onboarding.css';

const COMMON = {
  s1: { t: 'Bem-vindo ao Angular ERP', b: 'Gerencie obras, equipe e financeiro em um só lugar, de forma simples e organizada.' },
  s4: { t: 'Tudo pronto para começar', b: 'Em menos de 1 minuto, vamos mostrar as principais áreas do sistema.' },
};
const DATA = {
  admin: {
    s2: { t: 'Gestão completa da operação', b: 'Acompanhe obras, equipe, financeiro e relatórios em tempo real.' },
    s3: { t: 'Informações sempre atualizadas', b: 'Visualize indicadores de obras e financeiro com atualização automática.' },
    tour: [
      ['.sb-sub-item[data-page="obras"]', 'Crie, acompanhe e organize suas obras por aqui.'],
      ['.sb-sub-item[data-page="financeiro"]', 'Gerencie receitas, despesas e pagamentos.'],
      ['.sb-sub-item[data-page="relatorios"]', 'Visualize indicadores e relatórios da operação.'],
    ],
    tourTrial: [
      ['.sb-sub-item[data-page="obras"]', 'Crie, acompanhe e organize suas obras por aqui.'],
      ['.sb-sub-item[data-page="despesas"]', 'Registre e acompanhe as despesas das obras.'],
      ['.sb-sub-item[data-page="usuarios"]', 'Aqui estão os usuários demo para testar o app de campo da equipe.'],
      ['.sb-sub-item[data-page="upgrade"]', 'Quando estiver pronto, assine por aqui — seus dados continuam.'],
    ],
  },
  supervisor: {
    s2: { t: 'Sua equipe sob acompanhamento', b: 'Acompanhe o andamento das obras, equipe e registros de ponto em qualquer lugar.' },
    s3: { t: 'Gestão diária da equipe', b: 'Consulte presença, ponto e ocorrências da equipe em um único painel.' },
    tour: [
      ['.sbnav-btn[data-page="operacoes"]', 'Acompanhe tarefas e andamento das obras.'],
      ['.sbnav-btn[data-page="ponto"]', 'Consulte e registre o ponto por aqui.'],
      ['.sbnav-btn[data-page="perfil"]', 'Gerencie seus dados e preferências da conta.'],
    ],
  },
  funcionario: {
    s2: { t: 'Tudo o que você precisa no dia a dia', b: 'Registre seu ponto, acompanhe tarefas e acesse suas informações rapidamente.' },
    s3: { t: 'Registro de ponto simplificado', b: 'Faça seus registros com segurança, localização e conformidade com a CLT.' },
    tour: [
      ['.fbnav-btn[data-page="ponto"]', 'Registre sua entrada e saída por aqui.'],
      ['.fbnav-btn[data-page="historico"]', 'Consulte seu histórico de ponto e horas trabalhadas.'],
      ['.fbnav-btn[data-page="perfil"]', 'Acesse seus dados e preferências da conta.'],
    ],
  },
  cliente: {
    s2: { t: 'Acompanhe sua obra em tempo real', b: 'Veja o andamento, fotos e documentos da obra sempre que precisar.' },
    s3: { t: 'Atualizações centralizadas', b: 'Receba fotos, atualizações e informações da obra diretamente no portal.' },
    tour: [
      ['.cbnav-btn[data-page="painel"]', 'Acompanhe atualizações e fotos da sua obra por aqui.'],
      ['.cbnav-btn[data-page="obras"]', 'Veja etapas, detalhes e progresso da obra.'],
      ['.cbnav-btn[data-page="arquivos"]', 'Acesse contratos e documentos da obra.'],
    ],
  },
};
const TRIAL_SLIDE = {
  admin: { t: 'Você está no teste grátis', b: 'Alguns módulos ficam de fora do trial — assine um plano para liberar tudo. Para ver o app que sua equipe usa em campo, entre com os usuários demo da tela Usuários.' },
  campo: { t: 'Ambiente de demonstração', b: 'No teste grátis o app de campo é somente leitura — itens com 🔒 ficam disponíveis ao assinar. Com o plano ativo, a equipe registra ponto, RDO e despesas por aqui.' },
};

const LOGO = '<svg viewBox="0 0 218 218" aria-hidden="true"><use href="#logo-angular-grad"/></svg>';
const LOGO_W = '<svg viewBox="0 0 218 218" aria-hidden="true" style="color:#fff"><use href="#logo-angular"/></svg>';

function _role() {
  if (tenant.isCliente) return 'cliente';
  if (tenant.isFuncionario) return 'funcionario';
  if (tenant.isSupervisor) return 'supervisor';
  return 'admin';
}
function _tourable(role) {
  if (role === 'admin') return !!document.querySelector('.sb-nav'); // desktop shell montado
  return document.body.classList.contains('modo-' + role);
}

let _repos = null;
function _cleanup() {
  if (!_repos) return;
  window.removeEventListener('resize', _repos);
  window.removeEventListener('scroll', _repos, true);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', _repos);
    window.visualViewport.removeEventListener('scroll', _repos);
  }
  _repos = null;
}
function _close() {
  _cleanup();
  document.getElementById('ob-ov')?.remove();
  document.getElementById('ob-tour')?.remove();
}
let _seen = false;
function _finish(replay) {
  _close();
  if (replay) return;
  _seen = true;
  try { sb.rpc('marcar_onboarding_visto').then(() => {}, (e) => console.error('[onboarding] rpc', e)); }
  catch (e) { console.error('[onboarding] rpc', e); }
}

function _slides(role) {
  const arr = [COMMON.s1, DATA[role].s2, DATA[role].s3];
  if (tenant.isTrial) arr.push(role === 'admin' ? TRIAL_SLIDE.admin : TRIAL_SLIDE.campo);
  arr.push(COMMON.s4);
  return arr;
}

export function startOnboarding(opts = {}) {
  const role = DATA[_role()] ? _role() : 'admin';
  _close();
  const slides = _slides(role);
  const n = slides.length;
  let i = 0;
  const ov = document.createElement('div');
  ov.id = 'ob-ov';
  ov.className = 'sc-scope' + (document.documentElement.classList.contains('light') ? '' : ' dark');
  ov.setAttribute('role', 'dialog');
  ov.setAttribute('aria-modal', 'true');

  function paint() {
    const s = slides[i];
    let dots = '';
    for (let k = 0; k < n; k++) dots += `<span class="ob-dot${k === i ? ' on' : ''}"></span>`;
    const last = i === n - 1;
    ov.innerHTML = `<div class="ob-card"><div class="ob-bar"></div><div class="ob-bd">
      <div class="ob-hd"><span class="ob-mk">${LOGO_W}</span><span class="ob-eb">Angular ERP</span>
        <button class="ob-skip" id="ob-skip">Pular</button></div>
      <div class="ob-hero"><div class="ob-circ">${LOGO}</div>
        <h3 class="ob-ttl">${s.t}</h3><p class="ob-txt">${s.b}</p></div>
      <div class="ob-dots">${dots}</div>
      <div class="ob-ft"><button class="ob-bt" id="ob-prev" style="visibility:${i === 0 ? 'hidden' : 'visible'}">Anterior</button>
        <span class="ob-sp"></span>
        <button class="ob-bt pri" id="ob-next">${last ? 'Começar' : 'Próximo'}</button></div>
    </div></div>`;
    document.getElementById('ob-skip').onclick = () => _finish(opts.replay);
    document.getElementById('ob-prev').onclick = () => { if (i > 0) { i--; paint(); } };
    document.getElementById('ob-next').onclick = () => {
      if (i < n - 1) { i++; paint(); return; }
      _close();
      if (_tourable(role)) _tour(role, opts); else _finish(opts.replay);
    };
  }
  document.body.appendChild(ov);
  paint();
}

function _vis(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 4 && r.height > 4 && r.bottom > 0 && r.right > 0
    && r.top < (window.innerHeight || 0) && r.left < (window.innerWidth || 0);
}
function _find(sel) {
  const el = document.querySelector(sel);
  if (_vis(el)) return el;
  if (el) {
    // item dentro de grupo recolhido da sidebar → reabre via classe (sem toggle,
    // evita corrida com o handler/auto-recolhimento da própria sidebar)
    const g = el.closest('.sb-group');
    if (g && !g.classList.contains('is-open')) g.classList.add('is-open');
    if (_vis(el)) return el;
  }
  return null;
}
function _home(role) {
  try {
    if (role === 'admin') navigate('dashboard');
    else if (role === 'supervisor') document.querySelector('.sbnav-btn[data-page="operacoes"]')?.click();
    else if (role === 'funcionario') document.querySelector('.fbnav-btn[data-page="inicio"]')?.click();
    else if (role === 'cliente') document.querySelector('.cbnav-btn[data-page="painel"]')?.click();
  } catch (_) {}
}

function _tour(role, opts = {}) {
  const steps = (tenant.isTrial && DATA[role].tourTrial) || DATA[role].tour || [];
  let j = 0;
  const tv = document.createElement('div');
  tv.id = 'ob-tour';
  tv.className = 'sc-scope' + (document.documentElement.classList.contains('light') ? '' : ' dark');
  document.body.appendChild(tv);
  // Durante o tour: grupos da sidebar todos abertos (posições estáveis) e
  // auto-recolhimento suspenso (sidebar checa window.__obTourAtivo).
  window.__obTourAtivo = true;
  if (role === 'admin') document.querySelectorAll('.sb-group').forEach((g) => g.classList.add('is-open'));
  let _tick = null;

  function done() {
    _cleanup();
    if (_tick) { clearInterval(_tick); _tick = null; }
    window.__obTourAtivo = false;
    tv.remove();
    _home(role);
    _finish(opts.replay);
  }
  function place() {
    if (j < 0 || j >= steps.length) return false;
    const el = _find(steps[j][0]);
    if (!el) return false;
    let r = el.getBoundingClientRect();
    // item da sidebar atrás do rodapé fixo (.sb-foot)? scrolla o nav até liberar
    const foot = document.querySelector('.sb-foot');
    const nav = el.closest('.sb-nav');
    if (foot && nav) {
      const f = foot.getBoundingClientRect();
      if (r.bottom > f.top - 4) {
        nav.scrollTop += (r.bottom - f.top + 18);
        r = el.getBoundingClientRect();
      }
    }
    const pad = 6;
    const sx = Math.max(4, r.left - pad), sy = Math.max(4, r.top - pad);
    const sw = r.width + pad * 2, sh = r.height + pad * 2;
    const vw = window.innerWidth || 360, vh = window.innerHeight || 640;
    const sp = document.getElementById('ob-spot');
    if (sp) { sp.style.left = `${Math.round(sx)}px`; sp.style.top = `${Math.round(sy)}px`; sp.style.width = `${Math.round(sw)}px`; sp.style.height = `${Math.round(sh)}px`; }
    const tip = document.getElementById('ob-tip');
    if (tip) {
      const tr = tip.getBoundingClientRect();
      let ty = sy + sh + 12;
      if (ty + tr.height > vh - 10) ty = Math.max(10, sy - tr.height - 12);
      const tx = Math.min(Math.max(10, sx + sw / 2 - tr.width / 2), vw - tr.width - 10);
      tip.style.left = `${Math.round(tx)}px`; tip.style.top = `${Math.round(ty)}px`;
    }
    return true;
  }
  function show() {
    const el = _find(steps[j][0]);
    if (!el) { j++; if (j >= steps.length) { done(); return; } return show(); }
    try { el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' }); } catch (_) {}
    const last = j === steps.length - 1;
    tv.innerHTML = `<div class="ob-spot" id="ob-spot"></div><div class="ob-tip" id="ob-tip">
      <div class="ob-ts">Passo ${j + 1} de ${steps.length}</div><p class="ob-tt">${steps[j][1]}</p>
      <div class="ob-tf"><button class="ob-bt" id="ob-tskip">Pular tour</button><span class="ob-sp"></span>
      <button class="ob-bt pri" id="ob-tnext">${last ? 'Entendi' : 'Próximo'}</button></div></div>`;
    place();
    document.getElementById('ob-tskip').onclick = done;
    document.getElementById('ob-tnext').onclick = () => { j++; if (j >= steps.length) done(); else step(); };
    _cleanup();
    _repos = () => { try { place(); } catch (_) {} };
    window.addEventListener('resize', _repos);
    window.addEventListener('scroll', _repos, true);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', _repos);
      window.visualViewport.addEventListener('scroll', _repos);
    }
    setTimeout(place, 300); setTimeout(place, 800); setTimeout(place, 1500);
    requestAnimationFrame(() => requestAnimationFrame(place));
    if (_tick) clearInterval(_tick);
    _tick = setInterval(() => { try { place(); } catch (_) {} }, 400);
  }
  function step() {
    while (j < steps.length && !_find(steps[j][0])) j++;
    if (j >= steps.length) { done(); return; }
    const el = _find(steps[j][0]);
    tv.innerHTML = '';
    try { el.click(); } catch (_) {}
    setTimeout(show, 360);
  }
  step();
}

/** Dispara o onboarding 1x se o perfil ainda não viu. Chamar após o shell montar. */
export function maybeStartOnboarding() {
  try {
    if (_seen || tenant.profile?.onboarding_visto === true || tenant.isSuperAdmin) return;
    if (!tenant.isHydrated || !tenant.profile) return;
    setTimeout(() => { if (!document.getElementById('ob-ov') && !document.getElementById('ob-tour')) startOnboarding(); }, 800);
  } catch (e) { console.error('[onboarding]', e); }
}

// Replay manual (Configurações → "Ver introdução")
window.verIntroducao = () => startOnboarding({ replay: true });
